import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BarChart3, TrendingUp, Users, UserCheck,
  FileText, Activity, Network
} from '../../../components/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { isUserPartner } from '../../../lib/auth';
import { loadTeams, loadTeamResults, computeTeamAverages } from '../../../lib/teams';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { characteristics } from '../../../data/characteristics';
import type { TeamAverageScores } from '../../../types/team';

interface AnalyticsData {
  totalCoaches: number;
  totalRespondents: number;
  totalAssessments: number;
  totalTeams: number;
  codesUsed: number;
  codesAvailable: number;
  recentAssessments: { date: string; count: number }[];
  teamAverages: TeamAverageScores[];
}

export function PartnerAnalyticsTR() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    totalCoaches: 0,
    totalRespondents: 0,
    totalAssessments: 0,
    totalTeams: 0,
    codesUsed: 0,
    codesAvailable: 0,
    recentAssessments: [],
    teamAverages: [],
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/tr/login');
      return;
    }

    const init = async () => {
      try {
        const isPartner = await isUserPartner(user.id);
        if (!isPartner) {
          navigate('/tr/login');
          return;
        }
        await loadAnalytics(user.id);
      } catch (err) {
        console.error('Error checking partner status:', err);
        navigate('/tr/login');
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const loadAnalytics = async (userId: string) => {
    try {
      const { data: partner } = await supabase
        .from('partners')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();

      if (!partner) return;

      const { data: coaches } = await supabase
        .from('coaches')
        .select('id, user_id')
        .eq('partner_id', partner.id);

      const coachIds = (coaches || []).map(c => c.id);

      let totalRespondents = 0;
      let totalAssessments = 0;
      const last7Days: Record<string, number> = {};
      const today = new Date();
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        last7Days[date.toISOString().split('T')[0]] = 0;
      }

      if (coachIds.length > 0) {
        const { count: respondentCount } = await supabase
          .from('respondents')
          .select('id', { count: 'exact', head: true })
          .in('coach_id', coachIds);
        totalRespondents = respondentCount || 0;

        const { data: results } = await supabase
          .from('results')
          .select('completed_at')
          .in('coach_id', coachIds);
        totalAssessments = (results || []).length;

        (results || []).forEach(result => {
          if (result.completed_at) {
            const dateKey = new Date(result.completed_at).toISOString().split('T')[0];
            if (last7Days.hasOwnProperty(dateKey)) {
              last7Days[dateKey]++;
            }
          }
        });
      }

      const { data: codes } = await supabase
        .from('assessment_codes')
        .select('is_used')
        .eq('partner_id', partner.id);

      const codesUsed = (codes || []).filter(c => c.is_used).length;
      const codesAvailable = (codes || []).filter(c => !c.is_used).length;

      const teams = await loadTeams(partner.id);
      const teamAverages: TeamAverageScores[] = [];
      for (const team of teams.filter(t => t.type === 'team')) {
        const results = await loadTeamResults(team.id);
        if (results.length > 0) {
          teamAverages.push(computeTeamAverages(team.id, team.name, results));
        }
      }

      setAnalytics({
        totalCoaches: (coaches || []).length,
        totalRespondents,
        totalAssessments,
        totalTeams: teams.length,
        codesUsed,
        codesAvailable,
        recentAssessments: Object.entries(last7Days).map(([date, count]) => ({ date, count })),
        teamAverages,
      });
    } catch (err) {
      console.error('Error loading analytics:', err);
    }
  };

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Analitik yukleniyor..." />;
  }

  const statCards = [
    { label: 'Toplam Koclar', value: analytics.totalCoaches, icon: UserCheck, color: 'bg-teal-500' },
    { label: 'Toplam Katilimcilar', value: analytics.totalRespondents, icon: Users, color: 'bg-blue-500' },
    { label: 'Takimlar', value: analytics.totalTeams, icon: Network, color: 'bg-amber-500' },
    { label: 'Tamamlanan Degerlendirmeler', value: analytics.totalAssessments, icon: FileText, color: 'bg-emerald-500' },
    { label: 'Mevcut Kodlar', value: analytics.codesAvailable, icon: FileText, color: 'bg-rose-500' }
  ];

  const maxAssessments = Math.max(...analytics.recentAssessments.map(d => d.count), 1);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-xl">
              <BarChart3 className="w-6 h-6 text-teal-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Partner Analitik</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 ${stat.color} rounded-xl`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
              <div className="text-3xl font-bold text-slate-900 mb-1">{stat.value}</div>
              <div className="text-slate-500">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200"
          >
            <div className="flex items-center gap-2 mb-6">
              <Activity className="w-5 h-5 text-teal-600" />
              <h2 className="text-lg font-semibold text-slate-900">Degerlendirmeler (Son 7 Gun)</h2>
            </div>
            <div className="space-y-3">
              {analytics.recentAssessments.map((day) => (
                <div key={day.date} className="flex items-center gap-4">
                  <div className="w-20 text-sm text-slate-600">
                    {new Date(day.date).toLocaleDateString('tr-TR', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </div>
                  <div className="flex-1 h-8 bg-slate-100 rounded-lg overflow-hidden">
                    <div
                      className="h-full bg-teal-500 rounded-lg transition-all"
                      style={{ width: `${(day.count / maxAssessments) * 100}%` }}
                    />
                  </div>
                  <div className="w-8 text-sm font-medium text-slate-900 text-right">
                    {day.count}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200"
          >
            <div className="flex items-center gap-2 mb-6">
              <FileText className="w-5 h-5 text-teal-600" />
              <h2 className="text-lg font-semibold text-slate-900">Kod Kullanimi</h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 rounded-xl">
                <div className="text-3xl font-bold text-green-700">{analytics.codesUsed}</div>
                <div className="text-green-600">Kullanilan Kodlar</div>
              </div>
              <div className="p-4 bg-amber-50 rounded-xl">
                <div className="text-3xl font-bold text-amber-700">{analytics.codesAvailable}</div>
                <div className="text-amber-600">Mevcut Kodlar</div>
              </div>
            </div>
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-600">Kullanim Orani</span>
                <span className="font-medium text-slate-900">
                  {analytics.codesUsed + analytics.codesAvailable > 0
                    ? Math.round((analytics.codesUsed / (analytics.codesUsed + analytics.codesAvailable)) * 100)
                    : 0}%
                </span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-teal-500 rounded-full transition-all"
                  style={{
                    width: `${analytics.codesUsed + analytics.codesAvailable > 0
                      ? (analytics.codesUsed / (analytics.codesUsed + analytics.codesAvailable)) * 100
                      : 0}%`
                  }}
                />
              </div>
            </div>
          </motion.div>
        </div>

        {analytics.teamAverages.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 mt-6"
          >
            <div className="flex items-center gap-2 mb-6">
              <Network className="w-5 h-5 text-teal-600" />
              <h2 className="text-lg font-semibold text-slate-900">Takim Performans Karsilastirmasi</h2>
            </div>
            <p className="text-sm text-slate-500 mb-6">
              Takimlar arasi ortalama ozellik puanlari ({analytics.teamAverages.length} takim{analytics.teamAverages.length !== 1 ? '' : ''} sonuclu)
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Ozellik</th>
                    {analytics.teamAverages.map(ta => (
                      <th key={ta.teamId} className="text-center py-3 px-4 font-semibold text-slate-700">
                        {ta.teamName}
                        <div className="text-xs font-normal text-slate-400">{ta.membersWithResults} uye</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {characteristics.map((char) => (
                    <tr key={char.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 font-medium text-slate-800">{char.name}</td>
                      {analytics.teamAverages.map(ta => {
                        const score = ta.averageScores.find(s => s.characteristicId === char.id);
                        const value = score ? Math.round(score.normalized) : 0;
                        return (
                          <td key={ta.teamId} className="py-3 px-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-teal-500 rounded-full"
                                  style={{ width: `${value}%` }}
                                />
                              </div>
                              <span className="font-medium text-slate-700 w-8 text-right">{value}</span>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
